import { useQuery } from '@tanstack/react-query';
import { isNil } from 'lodash';
import { ApiService } from 'api';
import {
  MEASUREMENT_DATES_BY_FUND_DEFAULT_ERROR_MESSAGE,
  MEASUREMENT_DATES_BY_FUND_QUERY_KEY,
  SERVICES_FUNDS_REFETCH_ON_WINDOW_FOCUS,
} from 'common/constants/services/funds';
import { throwError } from 'utilities';
import {
  GetMeasurementDatesByFundParams,
  GetMeasurementDatesByFundResponse,
  GetMeasurementDatesByFundReturn,
  UseGetMeasurementDatesByFundParams,
} from './useGetMeasurementDatesByFund.types';

const getMeasurementDatesByFund = async (
  params: GetMeasurementDatesByFundParams
): Promise<GetMeasurementDatesByFundResponse | null> => {
  const { fundId } = params;

  if (isNil(fundId)) {
    return null;
  }

  try {
    const measurementDates = await ApiService.apiMeasurementDatesFundsRead(fundId.toString());

    if (!measurementDates?.results) {
      return throwError(MEASUREMENT_DATES_BY_FUND_DEFAULT_ERROR_MESSAGE);
    }

    return measurementDates.results;
  } catch (error) {
    return throwError(MEASUREMENT_DATES_BY_FUND_DEFAULT_ERROR_MESSAGE, error);
  }
};

const useGetMeasurementDatesByFund = (params: UseGetMeasurementDatesByFundParams): GetMeasurementDatesByFundReturn => {
  const { fund, shouldQueryAutomatically = true } = params;

  const fundId = fund?.id;

  const { data, error, isError, isFetching, isLoading, refetch } = useQuery<
    GetMeasurementDatesByFundReturn['data'],
    Error
  >({
    enabled: !isNil(fundId) && shouldQueryAutomatically,
    queryFn: () => getMeasurementDatesByFund({ fundId }),
    queryKey: [MEASUREMENT_DATES_BY_FUND_QUERY_KEY, fundId],
    refetchOnWindowFocus: SERVICES_FUNDS_REFETCH_ON_WINDOW_FOCUS,
  });

  return {
    data,
    error,
    isError,
    isFetching,
    isLoading,
    refetch,
  };
};

export default useGetMeasurementDatesByFund;
